import React from 'react';
import { OpcaoDeFatura, semClienteParaEtiqueta } from '../utils/faturaDoCdr';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { campoComEstado } from '../design';

/**
 * "Aplicar CDR na fatura", no modal Processar CDR: só as faturas do cliente do bloco, cada
 * uma dizendo o estado no rótulo, e a escolhida marcada em âmbar quando já tem nota
 * autorizada ou pagamento. Quem está com pressa lê cor, não frase. Ver utils/faturaDoCdr.
 */
interface Props {
  clientTag: string;
  bloco: { cliente: unknown | null; opcoes: OpcaoDeFatura[] };
  valor: number;
  onEscolher: (invoiceId: number) => void;
  /** O botão de aplicar, ao lado do select. */
  children?: React.ReactNode;
}

export default function SeletorDeFaturaDoCdr({ clientTag, bloco, valor, onEscolher, children }: Props) {
  if (!bloco.cliente) {
    return (
      <p className={`rounded-lg border px-3 py-2 text-xs ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`} role='status' data-testid='cdr-sem-cliente'>
        {semClienteParaEtiqueta(clientTag)}
      </p>
    );
  }
  const escolhida = bloco.opcoes.find((o) => o.id === valor) || null;
  const marcas = escolhida
    ? [
      escolhida.temNotaAutorizada ? `${escolhida.notasAutorizadas.join(', ')} ${escolhida.notasAutorizadas.length === 1 ? 'autorizada' : 'autorizadas'}` : '',
      escolhida.notaNaoVerificada ? 'nota não verificada' : '',
      escolhida.pago > 0 ? 'com pagamento' : '',
    ].filter(Boolean)
    : [];
  return (
    <div className='space-y-2'>
      <div className='flex flex-col sm:flex-row gap-2'>
        <select
          aria-label={`Fatura para o CDR de ${clientTag}`}
          value={valor || ''}
          onChange={(e) => onEscolher(parseInt(e.target.value, 10) || 0)}
          className={`flex-1 px-3 py-2 rounded-lg text-sm ${campoComEstado(marcas.length > 0 && TONE_BORDER.warning)}`}
        >
          <option value=''>{bloco.opcoes.length ? 'Selecione a fatura...' : 'Este cliente não tem fatura que não esteja cancelada'}</option>
          {bloco.opcoes.map((o) => (
            <option key={o.id} value={o.id} className={o.temNotaAutorizada || o.notaNaoVerificada ? TONE_TEXT.warning : undefined}>
              {o.rotulo}
            </option>
          ))}
        </select>
        {children}
      </div>
      {marcas.length > 0 && (
        <p className='text-xs' data-testid='cdr-risco'>
          <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${TONE_BADGE.warning}`}>{marcas.join(' · ')}</span>
          <span className={`ml-2 ${TONE_TEXT.warning}`}>se o CDR mudar o valor, a fatura fica diferente do que já saiu</span>
        </p>
      )}
    </div>
  );
}
